import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useBilanQuery } from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import { formatAmount } from "../utils/formatAmount";

const Bilan: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Récupérer la date depuis l'URL ou utiliser la date du jour par défaut
  const getDateFromUrl = (): string => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      return dateParam;
    }
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Date dans l'input (peut être modifiée sans recharger)
  const [inputDate, setInputDate] = useState<string>(getDateFromUrl);
  // Date utilisée pour la query (mise à jour via le bouton)
  const [queryDate, setQueryDate] = useState<string>(getDateFromUrl);

  // Mettre à jour la date de query si elle change dans l'URL (par exemple via partage de lien)
  useEffect(() => {
    const urlDate = searchParams.get("date");
    if (urlDate && urlDate !== queryDate) {
      setQueryDate(urlDate);
      setInputDate(urlDate);
    }
  }, [searchParams, queryDate]);

  // Fonction pour appliquer la date sélectionnée
  const handleApplyDate = () => {
    setQueryDate(inputDate);
    // Mettre à jour l'URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("date", inputDate);
    setSearchParams(newSearchParams, { replace: true });
  };

  const { loading, data, error, refetch } = useBilanQuery({
    variables: {
      date: queryDate,
    },
    skip: !isAuthenticated,
  });


  if (authLoading || loading) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Bilan</h1>
        <div className="text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Bilan</h1>
        <div className="text-center text-red-500">
          Veuillez vous connecter pour accéder au bilan
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Bilan</h1>
        <ErrorMessage
          error={error}
          onRetry={() => refetch()}
          title="Erreur lors du chargement du bilan"
          className="mb-6"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Bilan</h1>
        <ErrorMessage
          onRetry={() => refetch()}
          title="Erreur lors du chargement du bilan - pas de données"
          className="mb-6"
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Bilan</h1>
        <div className="flex items-center gap-4">
          <label htmlFor="bilan-date" className="text-sm font-medium text-gray-700">
            Date du bilan :
          </label>
          <input
            id="bilan-date"
            type="date"
            value={inputDate}
            onChange={(e) => setInputDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleApplyDate}
            disabled={loading || inputDate === queryDate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Afficher
          </button>
        </div>
      </div>

      {/* Bilan comptable français : Actif à gauche, Passif à droite */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="grid grid-cols-2 border-b-2 border-gray-800">
          {/* En-tête Actif */}
          <div className="bg-gray-100 p-4 border-r border-gray-300">
            <h2 className="text-xl font-bold text-gray-900 text-center">ACTIF</h2>
          </div>
          {/* En-tête Passif */}
          <div className="bg-gray-100 p-4">
            <h2 className="text-xl font-bold text-gray-900 text-center">PASSIF</h2>
          </div>
        </div>

        <div className="grid grid-cols-2">
          {/* Colonne Actif */}
          <div className="border-r border-gray-300">
            {data.bilan.active && data.bilan.active.length > 0 ? (
              data.bilan.active.map((bilanItems, index) => (
                <div key={`active-${index}-${bilanItems.name}`} className="border-b border-gray-200">
                  {/* Titre de la section */}
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-800">{bilanItems.name}</h3>
                    <span className="font-semibold text-gray-800">{formatAmount(bilanItems.total)}</span>
                  </div>
                  {/* Items de la section */}
                  {bilanItems.items && bilanItems.items.length > 0 ? (
                    (() => {
                      const filteredItems = bilanItems.items.filter((item) => {
                        // Masquer les montants entre -1€ et 1€
                        const amountInCents = item.amount;
                        return amountInCents < -100 || amountInCents > 100;
                      });
                      return filteredItems.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                          {filteredItems.map((item) => (
                            <div
                              key={item.id}
                              className="px-4 py-2 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {item.name || item.account?.name || "Sans nom"}
                                  </p>
                                  {item.comment && (
                                    <p className="text-xs text-gray-500 mt-1">{item.comment}</p>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  <p className="text-sm font-semibold text-gray-900">
                                    {formatAmount(item.amount)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément.</p></div>
                      );
                    })()
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément.</p></div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément actif.</p></div>
            )}
            {/* Total Actif */}
            <div className="bg-gray-100 px-4 py-3 border-t-2 border-gray-400">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg text-gray-900">TOTAL ACTIF</span>
                <span className="font-bold text-lg text-gray-900">
                  {formatAmount(data.bilan.totalActive)}
                </span>
              </div>
            </div>
          </div>

          {/* Colonne Passif */}
          <div>
            {data.bilan.passive && data.bilan.passive.length > 0 ? (
              data.bilan.passive.map((bilanItems, index) => (
                <div key={`passive-${index}-${bilanItems.name}`} className="border-b border-gray-200">
                  {/* Titre de la section */}
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="font-semibold text-gray-800">{bilanItems.name}</h3>
                    <span className="font-semibold text-gray-800">{formatAmount(bilanItems.total)}</span>
                  </div>
                  {/* Items de la section */}
                  {bilanItems.items && bilanItems.items.length > 0 ? (
                    (() => {
                      const filteredItems = bilanItems.items.filter((item) => {
                        // Masquer les montants entre -1€ et 1€
                        const amountInCents = item.amount;
                        return amountInCents < -100 || amountInCents > 100;
                      });
                      return filteredItems.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                          {filteredItems.map((item) => (
                            <div
                              key={item.id}
                              className="px-4 py-2 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {item.name || item.account?.name || "Sans nom"}
                                  </p>
                                  {item.comment && (
                                    <p className="text-xs text-gray-500 mt-1">{item.comment}</p>
                                  )}
                                </div>
                                <div className="text-right ml-4">
                                  <p className="text-sm font-semibold text-gray-900">
                                    {formatAmount(item.amount)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément.</p></div>
                      );
                    })()
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément.</p></div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-4 bg-gray-50 rounded-sm border border-gray-200"><p className="text-gray-500 italic">Aucun élément passif.</p></div>
            )}
            {/* Total Passif */}
            <div className="bg-gray-100 px-4 py-3 border-t-2 border-gray-400">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg text-gray-900">TOTAL PASSIF</span>
                <span className="font-bold text-lg text-gray-900">
                  {formatAmount(data.bilan.totalPassive)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Résultat du bilan (Actif - Passif) */}
        <div className="grid grid-cols-2 border-t-2 border-gray-800">
          <div className="bg-blue-50 px-4 py-3 border-r border-gray-300">
            <div className="flex justify-between items-center">
              <span className="font-bold text-lg text-gray-900">RÉSULTAT</span>
              <span className={`font-bold text-lg ${
                (data.bilan.totalActive - data.bilan.totalPassive) >= 0 
                  ? "text-green-700" 
                  : "text-red-700"
              }`}>
                {formatAmount(data.bilan.totalActive + data.bilan.totalPassive)}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Actif - Passif</p>
          </div>
          <div className="bg-blue-50 px-4 py-3">
            {/* Colonne vide pour maintenir la symétrie */}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Bilan;
